import { computed, onBeforeUnmount, Ref, ref, watch } from 'vue';
import { ContatosService, ResponseContato } from '@/services/contatos/contatos';
import { IdContato, IdInbox } from '@/services/common-types';
import { AtendimentoAbertoPorContato, AtendimentosService } from '@/services/atendimentos/atendimentos.service';
import { useAuthStoreUser } from '@/stores/app/authUser';
import { useChatStore } from '@/stores/app/chatStore';
import { useContatoStore } from '@/stores/app/contatoStore';
import { usePermissoesStore } from '@/stores/app/permissoesStore';
import { ehPlataformaCompativelComTipoContato } from '@/utils/plataforma';
import { normalizarTermoBusca } from '@/utils/destacarBusca';
import type { EntradaEmCascata } from '@/utils/animacaoEntrada';
import { atendentesQueBloqueiam, nomesDosAtendentes } from '@/utils/atendimentoAbertoDeOutro';

/** Abaixo disso a busca casaria meia agenda: não vale a ida ao servidor. */
export const MIN_CARACTERES_BUSCA_CONTATOS = 2;
const TAMANHO_PAGINA = 30;
const DEBOUNCE_MS = 300;

/**
 * Entrada e saída animadas.
 *
 * A linha leva QUANDO chegou, não o atraso pronto: o atraso calculado aqui ficaria
 * congelado no cache do `computed` e cada linha remontada pelo scroll do
 * `UseVirtualList` repetiria a cascata. O componente decide na montagem, com o
 * relógio daquele instante (`atrasoNaMontagem`).
 */
export type AnimacaoLinha = {
    /** Chegada da linha p/ a cascata; `null` = não entra em cascata. */
    entrada?: EntradaEmCascata | null;
    /** Linha da busca anterior saindo de cena: some com fade e não aceita clique. */
    saindo?: boolean;
};

/** Intervalo entre uma linha e a seguinte na entrada em cascata. */
const CASCATA_MS = 45;
/** Só as primeiras linhas da página entram em cascata: as demais estão fora da tela. */
const CASCATA_MAX_LINHAS = 12;
/** Duração do fade de saída (precisa casar com a animação do CSS). */
export const SAIDA_MS = 180;

export type InboxEmAtendimento = {
    id: IdInbox;
    nome: string;
    /** Nomes de quem atende, quando é OUTRA pessoa; `null` = em espera ou comigo. */
    atendidoPor: string | null;
    /** Todos os atendentes vinculados, com o próprio usuário marcado (tooltip). */
    atendentes: { id: string; nome: string; ehVoce: boolean }[];
    /** Conduzido por bot/agente de IA, sem humano. */
    isBot: boolean;
    /** O usuário não pode entrar neste atendimento (é de outro e falta `ver_todos`). */
    bloqueado: boolean;
};

export type LinhaContatoBusca = {
    itemType: 'contato';
    id: string;
    contato: ResponseContato;
    /** Inboxes (do usuário) onde o contato já tem atendimento em aberto. */
    inboxesEmAtendimento: InboxEmAtendimento[];
    /**
     * Motivo de a linha não ter ação: o inbox de destino está tomado por um
     * atendimento que o usuário não pode ver, e não sobra inbox livre. `null` =
     * clicável.
     */
    bloqueio: string | null;
} & AnimacaoLinha;

export type CabecalhoContatosBusca = {
    itemType: 'contatos-header';
    id: string;
    /** Nenhum atendimento casou a busca: o cabeçalho avisa antes dos contatos. */
    semAtendimentos: boolean;
} & AnimacaoLinha;

/** Próxima página de contatos a caminho: vai no FIM da lista, onde o scroll está. */
export type CarregandoContatosBusca = {
    itemType: 'contatos-carregando';
    id: string;
} & AnimacaoLinha;

export type LinhaBuscaContatos = LinhaContatoBusca | CabecalhoContatosBusca | CarregandoContatosBusca;

/**
 * Altura de uma linha no `UseVirtualList` da lista. Linha de atendimento/grupo e de
 * contato têm a altura de sempre (88); cabeçalho e linha de carregamento são mais baixos.
 */
export function alturaLinhaLista(item: { itemType?: string; semAtendimentos?: boolean } | undefined): number {
    if (item?.itemType === 'contatos-carregando') return 44;
    if (item?.itemType !== 'contatos-header') return 88;
    return item.semAtendimentos ? 72 : 44;
}

/**
 * Busca de contatos da lista de atendimentos.
 *
 * Paginação PRÓPRIA, de propósito: o `contatoStore` guarda a página e o termo do
 * ContactsModal, e reaproveitá-lo aqui faria a busca da lista apagar a do modal (e
 * vice-versa). Só o cache-buster de imagem é emprestado do store.
 */
export function useBuscaContatosLista(termo: Ref<string>) {
    const authStore = useAuthStoreUser();
    const chatStore = useChatStore();
    const contatoStore = useContatoStore();
    const permissoesStore = usePermissoesStore();
    const service = new ContatosService();
    const atendimentosService = new AtendimentosService();

    const contatos = ref<ResponseContato[]>([]);
    const total = ref(0);
    const pagina = ref(0);
    const carregando = ref(false);
    /**
     * Atendimentos abertos dos contatos carregados, vindos do servidor: só ele
     * sabe dos que estão com OUTROS atendentes quando o usuário não tem
     * `inbox.ver_todos_atendimentos` — esses não estão na lista dele.
     */
    const abertosPorContato = ref(new Map<IdContato, AtendimentoAbertoPorContato[]>());
    /** Quando cada contato chegou e em que posição da página: base da cascata. */
    let chegadas = new Map<IdContato, { em: number; ordem: number }>();
    /** Linhas da busca anterior, exibidas durante o fade de saída. */
    const linhasSaindo = ref<LinhaBuscaContatos[]>([]);
    let timerSaida: ReturnType<typeof setTimeout> | undefined;
    /** Última saída entregue à lista, para congelar na saída sem recalcular. */
    let ultimaSaida: LinhaBuscaContatos[] = [];
    /** Termo da última busca disparada; respostas de termos antigos são descartadas. */
    let termoAtual = '';
    let timer: ReturnType<typeof setTimeout> | undefined;

    const podeVerContatos = computed(() => {
        const idConta = authStore.currentConta?.id;
        return !!idConta && permissoesStore.temPermissao('conta.visualizar_contatos', idConta);
    });

    const termoNormalizado = computed(() => normalizarTermoBusca(termo.value));
    const ativo = computed(() => podeVerContatos.value && termoNormalizado.value.length >= MIN_CARACTERES_BUSCA_CONTATOS);
    const esgotado = computed(() => pagina.value > 0 && contatos.value.length >= total.value);

    function limpar() {
        // O que estava na tela sai com fade em vez de sumir de uma vez.
        if (ultimaSaida.length) {
            clearTimeout(timerSaida);
            linhasSaindo.value = ultimaSaida.map(l => ({ ...l, saindo: true, entrada: null }));
            ultimaSaida = [];
            timerSaida = setTimeout(() => { linhasSaindo.value = []; }, SAIDA_MS);
        }

        clearTimeout(timer);
        chegadas = new Map();
        termoAtual = '';
        contatos.value = [];
        abertosPorContato.value = new Map();
        total.value = 0;
        pagina.value = 0;
        carregando.value = false;
    }

    async function carregarPagina() {
        const idConta = authStore.currentConta?.id;
        if (!idConta || !termoAtual || carregando.value || esgotado.value) return;

        const termoDaRequisicao = termoAtual;
        carregando.value = true;
        try {
            const res = await service.getByContaPaginated(idConta, pagina.value + 1, TAMANHO_PAGINA, termoDaRequisicao);
            if (termoDaRequisicao !== termoAtual) return;

            // Antes de exibir a página: sem isto a linha nasceria clicável e só
            // depois apagaria, quando o atendimento de outro atendente chegasse.
            const abertos = await atendimentosService
                .findAbertosPorContatos(idConta, res.dados.map(c => c.id))
                .catch((e) => {
                    console.error('[useBuscaContatosLista] Falha ao buscar atendimentos abertos', e);
                    return [] as AtendimentoAbertoPorContato[];
                });
            if (termoDaRequisicao !== termoAtual) return;

            const mapa = new Map(abertosPorContato.value);
            for (const aberto of abertos) mapa.set(aberto.idContato, [...(mapa.get(aberto.idContato) ?? []), aberto]);
            abertosPorContato.value = mapa;

            const agora = Date.now();
            res.dados.forEach((c, ordem) => chegadas.set(c.id, { em: agora, ordem }));
            contatos.value.push(...res.dados.map(c => contatoStore._applyCacheBuster(c)));
            total.value = res.total;
            pagina.value = res.pagina;
        } catch (e) {
            console.error('[useBuscaContatosLista] Falha ao buscar contatos', e);
        } finally {
            if (termoDaRequisicao === termoAtual) carregando.value = false;
        }
    }

    watch([() => termo.value, ativo, () => authStore.currentConta?.id], () => {
        limpar();
        if (!ativo.value) return;

        const termoAgendado = termo.value;
        timer = setTimeout(() => {
            termoAtual = termoAgendado;
            void carregarPagina();
        }, DEBOUNCE_MS);
    }, { immediate: true });

    onBeforeUnmount(() => { clearTimeout(timer); clearTimeout(timerSaida); });

    /** Chegada desta posição p/ a cascata. Sem `Date.now()` aqui: isto vive num `computed`. */
    function entradaDe(chegada: { em: number; ordem: number } | undefined): EntradaEmCascata | null {
        if (!chegada) return null;
        return { chegouEm: chegada.em, atrasoMs: Math.min(chegada.ordem, CASCATA_MAX_LINHAS) * CASCATA_MS };
    }

    /** O atendimento aberto do contato neste inbox está na lista do próprio usuário? */
    function estaNaMinhaLista(contato: ResponseContato, idInbox?: IdInbox) {
        for (const [id, porContato] of chatStore.ongoingAtendimentosByContatoByInboxMap) {
            if ((idInbox === undefined || id == idInbox) && porContato.has(contato.id)) return true;
        }
        return false;
    }

    /**
     * Inboxes do usuário onde o contato tem atendimento aberto. Base = servidor
     * (vê os de outros atendentes); a lista local completa o que o servidor não
     * trouxe (rota indisponível, falha), sempre como desbloqueado — se está na
     * lista, o usuário pode entrar.
     */
    function inboxesEmAtendimentoDe(contato: ResponseContato): InboxEmAtendimento[] {
        const idConta = authStore.currentConta?.id;
        const idUsuario = authStore.user?.id;
        const encontrados = new Map<string, InboxEmAtendimento>();

        for (const aberto of abertosPorContato.value.get(contato.id) ?? []) {
            const inbox = authStore.currentAccountInboxes.find(i => i.id == aberto.idInbox);
            // Inbox a que o usuário não pertence não é destino possível: não informa nada aqui.
            if (!inbox) continue;

            const podeVerTodos = !!idConta && permissoesStore.temPermissao('inbox.ver_todos_atendimentos', idConta, inbox.id);
            const bloqueiam = estaNaMinhaLista(contato, inbox.id) ? null : atendentesQueBloqueiam(aberto, idUsuario, podeVerTodos);
            const deOutro = aberto.atendentes.filter(a => a.id != idUsuario);

            encontrados.set(String(inbox.id), {
                id: inbox.id,
                nome: inbox.nome,
                atendidoPor: deOutro.length ? nomesDosAtendentes(deOutro) : null,
                atendentes: aberto.atendentes.map(a => ({ id: String(a.id), nome: a.nome, ehVoce: a.id == idUsuario })),
                isBot: aberto.isBot,
                bloqueado: !!bloqueiam,
            });
        }

        for (const [idInbox, porContato] of chatStore.ongoingAtendimentosByContatoByInboxMap) {
            const idAtendimento = porContato.get(contato.id);
            if (!idAtendimento || encontrados.has(String(idInbox))) continue;

            const nome = authStore.currentAccountInboxes.find(i => i.id == idInbox)?.nome ?? 'Inbox';
            const chat = chatStore.chatList.find(c => c.id == idAtendimento);
            const atendentes = (chat?.atendentes ?? []).map(a => ({ id: String(a.id), nome: a.name, ehVoce: a.id == idUsuario }));
            const deOutro = atendentes.filter(a => !a.ehVoce);
            encontrados.set(String(idInbox), {
                id: idInbox,
                nome,
                atendidoPor: deOutro.length ? nomesDosAtendentes(deOutro) : null,
                atendentes,
                isBot: !!chat?.idBotAtivo || (!!chat?.idChatBotIa && chat?.statusIntegracaoIa !== 'EM_ATENDIMENTO_HUMANO'),
                bloqueado: false,
            });
        }

        return [...encontrados.values()];
    }

    /**
     * Linha sem ação quando não há para onde ir:
     * - destino inequívoco (um só inbox, ou um só filtrado) tomado por atendimento
     *   que o usuário não pode ver;
     * - destino ambíguo, mas TODOS os inboxes possíveis estão nessa situação.
     * Com algum inbox livre a linha fica clicável e o modal oferece só o livre.
     */
    function bloqueioDa(contato: ResponseContato, inboxes: InboxEmAtendimento[]): string | null {
        const motivo = (i: InboxEmAtendimento) => `Em atendimento com ${i.atendidoPor ?? 'outro atendente'}`;
        const porId = new Map(inboxes.map(i => [String(i.id), i]));

        const alvo = inboxSugerido(contato);
        if (alvo) {
            const noAlvo = porId.get(String(alvo));
            return noAlvo?.bloqueado ? motivo(noAlvo) : null;
        }

        const compativeis = inboxesCompativeis(contato);
        if (!compativeis.length) return null;
        const bloqueados = compativeis.map(i => porId.get(String(i.id))).filter(i => i?.bloqueado);
        return bloqueados.length === compativeis.length ? motivo(bloqueados[0]!) : null;
    }

    function inboxesCompativeis(contato: ResponseContato) {
        return authStore.currentAccountInboxes.filter(i => ehPlataformaCompativelComTipoContato(i.plataforma, contato.tipo));
    }

    /**
     * Linhas a renderizar.
     *
     * Contato já em atendimento:
     * - com UM inbox possível e o atendimento na lista do usuário, some: ele já está
     *   logo acima, e não há outro inbox por onde abrir um novo;
     * - com o atendimento de OUTRO atendente que o usuário não pode ver, fica
     *   apagado e sem ação quando não sobra inbox livre (ver `bloqueioDa`);
     * - nos demais casos fica com o badge do(s) inbox(es) em atendimento — o modal
     *   deixa ver aquele atendimento (se permitido) ou abrir outro por outro inbox.
     */
    const linhas = computed<LinhaContatoBusca[]>(() => {
        if (!ativo.value) return [];

        return contatos.value.flatMap(contato => {
            if (estaNaMinhaLista(contato) && inboxesCompativeis(contato).length <= 1) return [];
            const inboxesEmAtendimento = inboxesEmAtendimentoDe(contato);
            const bloqueio = bloqueioDa(contato, inboxesEmAtendimento);
            const entrada = entradaDe(chegadas.get(contato.id));
            return [{ itemType: 'contato' as const, id: `contato-${contato.id}`, contato, inboxesEmAtendimento, bloqueio, entrada }];
        });
    });

    // Página inteira escondida pela regra acima não aumenta a lista, e sem lista
    // crescendo não há scroll para pedir a próxima: busca a seguinte direto.
    watch(() => [linhas.value.length, pagina.value] as const, ([qtdLinhas, paginaAtual], anterior) => {
        if (!ativo.value || carregando.value || esgotado.value || paginaAtual === 0) return;
        if (anterior && anterior[1] !== paginaAtual && qtdLinhas === anterior[0]) void carregarPagina();
    });

    /**
     * Linhas prontas para irem ao fim da lista de atendimentos: cabeçalho + contatos.
     *
     * A seção só aparece quando a primeira página CHEGOU e trouxe contato: um
     * cabeçalho "Contatos" com spinner, que some quando a busca não acha nada,
     * fazia a lista pular a cada tecla. Páginas seguintes ganham uma linha de
     * carregamento no fim, que é onde o usuário está olhando.
     */
    function linhasAposAtendimentos(qtdAtendimentos: number): LinhaBuscaContatos[] {
        if (!ativo.value || !linhas.value.length) {
            ultimaSaida = [];
            return linhasSaindo.value;
        }
        // O cabeçalho entra junto com a primeira linha da primeira página.
        const primeira = linhas.value[0];
        const cabecalho: CabecalhoContatosBusca = {
            itemType: 'contatos-header',
            id: 'contatos-header',
            semAtendimentos: qtdAtendimentos === 0,
            // Atraso zero, mesma chegada da primeira linha: abre a cascata.
            entrada: primeira.entrada ? { chegouEm: primeira.entrada.chegouEm, atrasoMs: 0 } : null,
        };
        const carregandoMais: CarregandoContatosBusca[] = carregando.value ? [{ itemType: 'contatos-carregando', id: 'contatos-carregando' }] : [];
        ultimaSaida = [cabecalho, ...linhas.value, ...carregandoMais];
        return ultimaSaida;
    }

    /** Inbox a pré-selecionar no modal: só quando não há ambiguidade. */
    function inboxSugerido(contato: ResponseContato): IdInbox | undefined {
        const compativeis = inboxesCompativeis(contato);
        if (compativeis.length === 1) return compativeis[0].id;

        const filtrados = chatStore.listFilters.selectedInboxIds ?? [];
        if (filtrados.length === 1 && compativeis.some(i => i.id == filtrados[0])) return filtrados[0];

        return undefined;
    }

    function abrirAtendimentoPara(contato: ResponseContato) {
        // Redundante com a linha sem ação, de propósito: o clique pode vir de outro caminho.
        if (bloqueioDa(contato, inboxesEmAtendimentoDe(contato))) return;
        void chatStore.suggestToOpenNovoAtendimento({
            contactToFocus: contato,
            inboxToFocus: inboxSugerido(contato),
            waitForResponse: false,
        });
    }

    return {
        ativo,
        linhas,
        linhasAposAtendimentos,
        carregando,
        esgotado,
        carregarMais: carregarPagina,
        abrirAtendimentoPara,
    };
}
